import { all, call, fork, put, takeEvery, select } from "redux-saga/effects";
import moment from "moment";
import {
    INICIA_DIARIO,
    BUSCA_DIARIO,
    LANCA_FALTAS_DIARIO,
    LISTA_CURSOS_DIARIO,
    LISTA_SERIES_DIARIO,
    LISTA_TURMAS_DIARIO,
    ON_HIDE_LOADER,
    REABRIR_DIARIO,
    REABRIR_DIARIO_SUCESSO,
    FECHAR_DIARIO,
    FECHAR_DIARIO_SUCESSO
} from "constants/ActionTypes";
import requisicaoApi from 'util/Api';
import {
    inicioDiarioSucesso,
    buscaDiarioSucesso,
    listaCursosDiarioSucesso,
    listaSeriesDiarioSucesso,
    listaTurmasDiarioSucesso,
    lancaFaltasDiarioSucesso,
    reabrirDiarioSucesso,
    fecharDiarioSucesso,
    buscaDiario as buscaDiarioAction,
    showAuthMessage,
    showSuccessMessage
} from "actions/Diario";
import { onChangeCombo } from "actions/Global";

/*#################################################################################################################*/
/* --------------------------------------------- Requests ---------------------------------------------*/

const getEscolas = async () =>
    await requisicaoApi.get('/escolas')
        .then(resposta => resposta)
        .catch(error => error);

const getCursosPorEscolaAnoLetivo = async (escolaId, anoLetivo) =>
    await requisicaoApi.get(`/escolas/${escolaId}/cursos`, { params: { anoLetivo } })
        .then(resposta => resposta)
        .catch(error => error);

const getSeriesPorCurso = async (cursoId) =>
    await requisicaoApi.get('/series', { params: { CursoId: cursoId } })
        .then(resposta => resposta)
        .catch(error => error);

const getTurmasPorFiltros = async (ano, escolaId, serieId) =>
    await requisicaoApi.get('/turmas', { params: { Ano: ano, EscolaId: escolaId, SerieId: serieId } })
        .then(resposta => resposta)
        .catch(error => error);

const getDiarioDia = async (turmaId, anoLetivo, data) =>
    await requisicaoApi.get('/diario-escolar/diario-dia', { params: { turmaId, anoLetivo, data } })
        .then(resposta => resposta)
        .catch(error => error);

const postLancarFaltas = async (dados) =>
    await requisicaoApi.post('/diario-escolar/lancar-faltas', dados)
        .then(resposta => resposta)
        .catch(error => error);

const postReabrirDiario = async (id) =>
    await requisicaoApi.post(`/diario-escolar/${id}/reabrir`)
        .then(resposta => resposta)
        .catch(error => error);

const postFecharDiario = async (id) =>
    await requisicaoApi.post(`/diario-escolar/${id}/fechar`)
        .then(resposta => resposta)
        .catch(error => error);

/*#################################################################################################################*/
/* ------------------------------------------ Funções das Sagas ---------------------------------------------------*/

const extractErrorMessage = (error, mensagemPadrao) => {
    if (!error) return mensagemPadrao;
    const response = error.response;
    const status = response?.status;
    const responseData = response?.data;

    if (status === 400 && responseData) {
        const inconsistencias = responseData.inconsistencias || [];
        if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
            return inconsistencias[0].mensagem || mensagemPadrao;
        }
        if (responseData?.detalhe) return responseData.detalhe;
        if (responseData.mensagem) return responseData.mensagem;
        if (responseData.message) return responseData.message;
    }
    if (responseData?.detalhe) return responseData.detalhe;
    if (responseData?.mensagem) return responseData.mensagem;
    if (responseData?.message) return responseData.message;
    if (error.message) return error.message;

    return mensagemPadrao;
};

function* fnIniciaDiario() {
    try {
        const retorno = yield call(getEscolas);
        if (retorno.message || retorno.response) {
            const mensagem = extractErrorMessage(retorno, 'Erro ao buscar escolas');
            yield put(showAuthMessage(mensagem));
        } else {
            const data = retorno.data;
            const escolas = Array.isArray(data) ? data : (Array.isArray(data?.escolas) ? data.escolas : []);
            yield put(inicioDiarioSucesso(escolas));
        }
    } catch (error) {
        const mensagem = extractErrorMessage(error, 'Erro ao buscar escolas');
        yield put(showAuthMessage(mensagem));
    } finally {
        yield put({ type: ON_HIDE_LOADER });
    }
}

function* fnListaCursosDiario({ payload }) {
    try {
        const { escolaId, anoLetivo } = payload;
        if (!escolaId) {
            yield put(listaCursosDiarioSucesso([]));
            return;
        }
        const retorno = yield call(getCursosPorEscolaAnoLetivo, escolaId, anoLetivo);
        if (retorno.message || retorno.response) {
            const mensagem = extractErrorMessage(retorno, 'Erro ao listar cursos');
            yield put(showAuthMessage(mensagem));
        } else {
            const data = retorno.data;
            const cursos = Array.isArray(data) ? data : (Array.isArray(data?.cursos) ? data.cursos : (Array.isArray(data?.Cursos) ? data.Cursos : []));
            yield put(listaCursosDiarioSucesso(cursos));
        }
    } catch (error) {
        const mensagem = extractErrorMessage(error, 'Erro ao listar cursos');
        yield put(showAuthMessage(mensagem));
    }
}

function* fnListaSeriesDiario({ payload }) {
    try {
        const { cursoId } = payload;
        if (!cursoId) {
            yield put(listaSeriesDiarioSucesso([]));
            return;
        }
        const retorno = yield call(getSeriesPorCurso, cursoId);
        if (retorno.message || retorno.response) {
            const mensagem = extractErrorMessage(retorno, 'Erro ao listar séries');
            yield put(showAuthMessage(mensagem));
        } else {
            const data = retorno.data;
            const series = Array.isArray(data) ? data : (Array.isArray(data?.series) ? data.series : (Array.isArray(data?.Series) ? data.Series : []));
            yield put(listaSeriesDiarioSucesso(series));
        }
    } catch (error) {
        const mensagem = extractErrorMessage(error, 'Erro ao listar séries');
        yield put(showAuthMessage(mensagem));
    }
}

function* fnListaTurmasDiario({ payload }) {
    try {
        const { ano, escolaId, serieId } = payload;
        if (!ano || !escolaId || !serieId) {
            yield put(listaTurmasDiarioSucesso([]));
            return;
        }
        const retorno = yield call(getTurmasPorFiltros, ano, escolaId, serieId);
        if (retorno.message || retorno.response) {
            const mensagem = extractErrorMessage(retorno, 'Erro ao listar turmas');
            yield put(showAuthMessage(mensagem));
        } else {
            const data = retorno.data;
            const turmas = Array.isArray(data) ? data : (Array.isArray(data?.turmas) ? data.turmas : (Array.isArray(data?.Turmas) ? data.Turmas : []));
            yield put(listaTurmasDiarioSucesso(turmas));
        }
    } catch (error) {
        const mensagem = extractErrorMessage(error, 'Erro ao listar turmas');
        yield put(showAuthMessage(mensagem));
    }
}

function* fnBuscaDiario({ payload }) {
    try {
        const { turmaId, anoLetivo, data } = payload;
        const retorno = yield call(getDiarioDia, turmaId, anoLetivo, data);
        if (retorno.message || retorno.response) {
            const mensagem = extractErrorMessage(retorno, 'Erro ao buscar diário');
            yield put(showAuthMessage(mensagem));
        } else {
            yield put(buscaDiarioSucesso(retorno.data));
            if (retorno.data.idTurma) {
                yield put(onChangeCombo('turmaId', null, { id: retorno.data.idTurma }));
            }
        }
    } catch (error) {
        const mensagem = extractErrorMessage(error, 'Erro ao buscar diário');
        yield put(showAuthMessage(mensagem));
    } finally {
        yield put({ type: ON_HIDE_LOADER });
    }
}

function* fnLancaFaltasDiario({ payload }) {
    try {
        const retorno = yield call(postLancarFaltas, payload);
        if (retorno.message || retorno.response) {
            const mensagem = extractErrorMessage(retorno, 'Erro ao lançar faltas');
            yield put(showAuthMessage(mensagem));
        } else {
            yield put(lancaFaltasDiarioSucesso(retorno.data));
            yield put(showSuccessMessage('Faltas lançadas com sucesso!'));
        }
    } catch (error) {
        const mensagem = extractErrorMessage(error, 'Erro ao lançar faltas');
        yield put(showAuthMessage(mensagem));
    }
}

function* fnReabrirDiario({ payload }) {
    try {
        const retorno = yield call(postReabrirDiario, payload);
        if (retorno.message || retorno.response) {
            const mensagem = extractErrorMessage(retorno, 'Erro ao reabrir diário');
            yield put(showAuthMessage(mensagem));
        } else {
            yield put(reabrirDiarioSucesso());
            yield put(showSuccessMessage('Diário reaberto com sucesso!'));
            // Refetch diary to update state
            const state = yield select(state => state.diario);
            if (state) {
                const { turmaId, anoLetivo, data } = state;
                yield put(buscaDiarioAction({
                    turmaId: typeof turmaId === 'object' ? turmaId.id : turmaId,
                    anoLetivo,
                    data: moment(data, 'DD/MM/YYYY').format('YYYY-MM-DD')
                }));
            }
        }
    } catch (error) {
        const mensagem = extractErrorMessage(error, 'Erro ao reabrir diário');
        yield put(showAuthMessage(mensagem));
    } finally {
        yield put({ type: ON_HIDE_LOADER });
    }
}

function* fnFecharDiario({ payload }) {
    try {
        const retorno = yield call(postFecharDiario, payload);
        if (retorno.message || retorno.response) {
            const mensagem = extractErrorMessage(retorno, 'Erro ao fechar diário');
            yield put(showAuthMessage(mensagem));
        } else {
            yield put(fecharDiarioSucesso());
            yield put(showSuccessMessage('Diário fechado com sucesso!'));
            // Refetch diary to update state
            const state = yield select(state => state.diario);
            if (state) {
                const { turmaId, anoLetivo, data } = state;
                yield put(buscaDiarioAction({
                    turmaId: typeof turmaId === 'object' ? turmaId.id : turmaId,
                    anoLetivo,
                    data: moment(data, 'DD/MM/YYYY').format('YYYY-MM-DD')
                }));
            }
        }
    } catch (error) {
        const mensagem = extractErrorMessage(error, 'Erro ao fechar diário');
        yield put(showAuthMessage(mensagem));
    } finally {
        yield put({ type: ON_HIDE_LOADER });
    }
}

export function* iniciaDiario() {
    yield takeEvery(INICIA_DIARIO, fnIniciaDiario);
}

export function* listaCursosDiario() {
    yield takeEvery(LISTA_CURSOS_DIARIO, fnListaCursosDiario);
}

export function* listaSeriesDiario() {
    yield takeEvery(LISTA_SERIES_DIARIO, fnListaSeriesDiario);
}

export function* listaTurmasDiario() {
    yield takeEvery(LISTA_TURMAS_DIARIO, fnListaTurmasDiario);
}

export function* buscaDiario() {
    yield takeEvery(BUSCA_DIARIO, fnBuscaDiario);
}

export function* lancaFaltasDiario() {
    yield takeEvery(LANCA_FALTAS_DIARIO, fnLancaFaltasDiario);
}

export default function* rootSaga() {
    yield all([
        fork(iniciaDiario),
        fork(listaCursosDiario),
        fork(listaSeriesDiario),
        fork(listaTurmasDiario),
        fork(buscaDiario),
        fork(lancaFaltasDiario),
        takeEvery(REABRIR_DIARIO, fnReabrirDiario),
        takeEvery(FECHAR_DIARIO, fnFecharDiario)
    ]);
}
